<template>
  <div>
    <div class="tile is-ancestor">
      <div class="tile is-6 is-vertical is-parent">
        <div class="tile is-child card">
          <div class="card-image">
            <figure class="image is-16by9">
              <ClickableImage :src="require('../assets/vale-guardian.jpg')" alt="Vale Guardian" ratio="is-16by9"/>
            </figure>
          </div>
          <div class="card-content">
            <p class="title is-4">Vale Guardian</p>
            <b-field grouped group-multiline>
              <div class="control">
                <b-taglist attached>
                  <b-tag type="is-dark">Health</b-tag>
                  <b-tag type="is-info">22,021,440</b-tag>
                </b-taglist>
              </div>
              <div class="control">
                <b-taglist attached>
                  <b-tag type="is-dark">Tanking</b-tag>
                  <b-tag type="is-info">Toughness</b-tag>
                </b-taglist>
              </div>
              <div class="control">
                <b-taglist attached>
                  <b-tag type="is-dark">Hitbox</b-tag>
                  <b-tag type="is-info">Small</b-tag>
                </b-taglist>
              </div>
              <div class="control">
                <b-taglist attached>
                  <b-tag type="is-dark">Defiance bars</b-tag>
                  <b-tag type="is-success">Yes</b-tag>
                </b-taglist>
              </div>
              <div class="control">
                <b-taglist attached>
                  <b-tag type="is-dark">Enrage</b-tag>
                  <b-tag type="is-info">8:00</b-tag>
                  <b-tag type="is-light">+200% damage</b-tag>
                </b-taglist>
              </div>
              <div class="control">
                <b-taglist attached>
                  <b-tag type="is-dark">Challenge Mode</b-tag>
                  <b-tag type="is-danger">No</b-tag>
                </b-taglist>
              </div>
              <div class="control">
                <b-taglist attached>
                  <b-tag type="is-warning">Reduced Toughness</b-tag>
                </b-taglist>
              </div>
            </b-field>
          </div>
        </div>
        <div class="tile is-child card">
          <div class="card-content">
            <p class="title is-4">Phases</p>
            <p>
              The Vale Guardian fight has the following phases:
            </p>
            <div class="content">
              <ul>
                <li>Phase 1 (100&ndash;66%)</li>
                <li>Split phase (Red, Green, Blue Guardian)</li>
                <li>Phase 2 (66&ndash;33%)</li>
                <li>Split phase (Red, Green, Blue Guardian)</li>
                <li>Phase 3 (33&ndash;0%)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="tile is-parent is-vertical">
        <div class="tile is-child card">
          <div class="card-image">
            <figure class="image is-3by2">
              <ClickableImage :src="require('../assets/arena.jpg')" alt="The Vale Guardian Arena" ratio="is-3by2"/>
            </figure>
          </div>
          <div class="card-content is-italic">
            The Vale Guardian arena. Shiny pylons! Pocket Raptors nearby.
          </div>
        </div>
        <div class="tile is-child card">
          <div class="card-image">
            <figure class="image is-16by9">
              <ClickableImage :src="require('../assets/start-area.jpg')" alt="Starting area" ratio="is-16by9"/>
            </figure>
          </div>
          <div class="card-content is-italic">
            Starting the fight: enter the highlighted area or attack the Vale Guardian.
            Everyone outside the stone arena dies.
          </div>
        </div>
      </div>
    </div>

    <p class="title is-3">Mechanics</p>
    <div class="tile is-ancestor">
      <div class="tile is-4 is-vertical is-parent">
        <mechanic-card
            class="tile is-child"
            title="Greens"
            subtitle="Distributed Magic"
            :img="require('../assets/distributed-magic.jpg')"
            img-alt="Distributed Magic"
            image-class="is-3by2"
            unblockable
        >
          <p>
            Damages all players for 80% of their maximum health unless 4 players stand inside.
          </p>
          <template #details>
            <span class="title is-6">Location</span>
            <p>
              Appears in the section Vale Guardian stands in, the exact position is random.
            </p>
            <span class="title is-6">Timing</span>
            <p>
              A new circle appears every 16 seconds from the last one exploding. Takes 7 seconds from appearance to
              damage.
            </p>
            <span class="title is-6">Damage</span>
            <p>
              If 4 players stand inside, a small amount of damage is inflicted to them instead of everyone being damaged
              for 80% of their maximum health.
            </p>
            <span class="title is-6">Damage reduction</span>
            <p>
              Damage reduction and armor have no effect on the damage taken. It is avoidable through the use of
              invulnerability. Barrier may be used to partially absorb the damage as well.
            </p>
            <span class="title is-6">Split phase differences</span>
            <p>
              The green circles from the Blue Guardian in split phases work slightly differently, see details of the
              Blue Guardian below.
            </p>
          </template>
        </mechanic-card>
      </div>
      <div class="tile is-4 is-vertical is-parent">
        <mechanic-card
            class="tile is-child"
            title="Blues"
            subtitle="Unstable Magic Spike"
            :img="require('../assets/unstable-magic-spike.jpg')"
            img-alt="Unstable Magic Spike"
            image-class="is-3by2"
            blockable
            evadable
            warning-border
        >
          <p>
            Teleports players who stand in them to a random location and damages them.
          </p>
          <template #details>
            <span class="title is-6">Location</span>
            <p>
              Appears under 5 players or minions closest to Vale Guardian; 10 targets in the last phase (33–0%).
              Does not appear under those evading or blocking.
            </p>
            <span class="title is-6">Timing</span>
            <p>
              Takes 2 seconds from appearance to teleport. Appears approximately every 9 seconds.
            </p>
          </template>
        </mechanic-card>
      </div>
      <div class="tile is-4 is-vertical is-parent">
        <mechanic-card
            class="tile is-child"
            title="Seekers"
            subtitle="Seeker"
            :img="require('../assets/seeker.jpg')"
            img-alt="A seeker"
            image-class="is-3by2"
            blockable
            evadable
        >
          <p>
            Moves towards players, heavily damages them if they are in the red circle. Affected by crowd control.
          </p>
          <template #details>
            <span class="title is-6">Location</span>
            <p>
              One seeker spawns in the middle of each section, on the tile with a symbol.
              TODO: Last phase, one spawns in the middle as well.
            </p>
            <span class="title is-6">Timing</span>
            <p>
              Appears every 20 seconds, along with the previous set disappearing.
            </p>
            <span class="title is-6">Health</span>
            <p>
              Can be killed, has 649,260 health.
            </p>
            <span class="title is-6">Stealth</span>
            <p>
              Does not follow stealthed players.
            </p>
          </template>
        </mechanic-card>
      </div>
    </div>

    <div class="tile is-ancestor">
      <div class="tile is-8 is-parent">
        <div class="tile is-child">
          <p class="subtitle is-4">Phase 2 onwards</p>
        </div>
      </div>
      <div class="tile is-4 is-parent">
        <div class="tile is-child">
          <p class="subtitle is-4">Phase 3</p>
        </div>
      </div>
    </div>

    <div class="tile is-ancestor">
      <div class="tile is-8 is-vertical">
        <div class="tile is-vertical">
          <div class="tile">
            <div class="tile is-parent">
              <mechanic-card
                  class="tile is-child"
                  title="Magic Storm"
                  :img="require('../assets/todo.jpg')"
                  img-alt="Magic Storm"
                  breakbar="2000"
                  evadable
                  blockable
              >
                <p>
                  Releases orbs that damage players around their impact point until the
                  defiance bar is broken.
                </p>
                <template #details>
                  <span class="title is-6">Fixation</span>
                  <p>
                    Vale Guardian turns towards the first player to enter the arena during this attack.
                  </p>
                  <span class="title is-6">Timing</span>
                  <p>
                    First cast happens 10 seconds into Phase 2. Repeats ~31 seconds after it last
                    finished.
                    The maximum duration is 8.5 seconds, Vale Guardian resumes normal attacks if not broken in time.
                  </p>
                </template>
              </mechanic-card>
            </div>
            <div class="tile is-parent">
              <mechanic-card
                  class="tile is-child"
                  title="Lit section"
                  subtitle="Unstable Pylon"
                  :img="require('../assets/todo.jpg')"
                  img-alt="Unstable Pylon"
              >
                <p>
                  One of the sections of the arena is lit, dealing damage to anyone standing on it. Sections change
                  every TODO seconds.
                </p>
                <template #details>
                  <span class="title is-6">Section order</span>
                  <p>
                    TODO: Order
                  </p>
                  <span class="title is-6">Timing</span>
                  <p>
                    TODO: Timing of swaps
                  </p>
                  <span class="title is-6">Damage</span>
                  <p>
                    The lit section always deals 2000 damage. TODO: Verify
                  </p>
                </template>
              </mechanic-card>
            </div>
          </div>
        </div>
      </div>

      <div class="tile is-4 is-vertical">
        <div class="tile is-parent is-vertical">
          <mechanic-card
              class="tile is-child"
              title="Two lit sections"
              subtitle="Unstable Pylon"
              :img="require('../assets/todo.jpg')"
              img-alt="Unstable Pylon"
          >
            <p>
              Two sections of the arena are lit, dealing damage to anyone standing on them. Sections change
              every TODO seconds.
            </p>
            <template #details>
              <span class="title is-6">Section order</span>
              <p>
                TODO: Order
              </p>
              <span class="title is-6">Timing</span>
              <p>
                TODO: Timing of swaps
              </p>
              <span class="title is-6">Damage</span>
              <p>
                The lit section always deals 2000 damage. TODO: Verify
              </p>
            </template>
          </mechanic-card>
        </div>
      </div>
    </div>

    <p class="title is-3">Split phases</p>
    <div class="tile is-ancestor">
      <div class="tile is-4 is-vertical is-parent">
        <mechanic-card
            class="tile is-child"
            title="Blue Guardian"
            subtitle="The one with greens"
            :img="require('../assets/todo.jpg')"
            img-alt="Blue Guardian"
        >
          <p>
            <!-- TODO: Protection icon -->
            Invulnerable unless Protection is removed;
            spawns a unique green circle.
          </p>
          <template #details>
            <span class="title is-6">Location</span>
            <p>
              Appears in front of the blue pylon.
            </p>
            <span class="title is-6">Defiance Bar</span>
            <p>
              <DefianceBar size="1000" />
              Gains a defiance bar when brought to zero health, this bar needs to be broken to kill the guardian.
            </p>
            <span class="title is-6">Timing</span>
            <p>
              The protection is reapplied every TODO seconds. The green hapens every TODO seconds.
            </p>
            <span class="title is-6">Health</span>
            <p>
              TODO
            </p>
            <span class="title is-6">Green differences</span>
            <p>
              The green circle spawned by the Blue Guardian only requires 3 players to avoid damage to everyone. Unlike
              the greens during main phases, the damage can be evaded.
            </p>
          </template>
        </mechanic-card>
      </div>
      <div class="tile is-4 is-vertical is-parent">
        <mechanic-card
            class="tile is-child"
            title="Green Guardian"
            subtitle="The one with blues"
            :img="require('../assets/todo.jpg')"
            img-alt="Green Guardian"
        >
          <p>
            Spawns blues under close players.
          </p>
          <template #details>
            <span class="title is-6">Location</span>
            <p>
              Appears in front of the green pylon.
            </p>
            <span class="title is-6">Defiance Bar</span>
            <p>
              <DefianceBar size="1000" />
              Gains a defiance bar when brought to zero health, this bar needs to be broken to kill the guardian.
            </p>
            <span class="title is-6">Timing</span>
            <p>
              TODO
            </p>
            <span class="title is-6">Health</span>
            <p>
              TODO
            </p>
            <span class="title is-6">Split differences</span>
            <p>
              TODO
            </p>
          </template>
        </mechanic-card>
      </div>
      <div class="tile is-4 is-vertical is-parent">
        <mechanic-card
            class="tile is-child"
            title="Red Guardian"
            subtitle="The condi one"
            :img="require('../assets/todo.jpg')"
            img-alt="Red Guardian"
        >
          <p>
            Only vulnerable to condition damage due to high toughness; spawns a seeker.
          </p>
          <template #details>
            <span class="title is-6">Location</span>
            <p>
              Appears in front of the red pylon.
            </p>
            <span class="title is-6">Defiance Bar</span>
            <p>
              <DefianceBar size="1000" />
              Gains a defiance bar when brought to zero health, this bar needs to be broken to kill the guardian.
            </p>
            <span class="title is-6">Timing</span>
            <p>
              TODO
            </p>
            <span class="title is-6">Health</span>
            <p>
              TODO
            </p>
            <span class="title is-6">Life steal</span>
            <p>
              Life steal ignores toughness and is another method capable of killing the Red Guardian, should you have
              enough of it.
            </p>
          </template>
        </mechanic-card>
      </div>
    </div>
    <div class="tile is-ancestor">
      <div class="tile is-8 is-vertical is-parent">
        <mechanic-card
            class="tile is-child"
            title="Pylon Attunement"
            subtitle=""
            :img="require('../assets/todo.jpg')"
            image-class="is-3by1"
            img-alt="Pylon attunements"
        >
          <p>
            Each player is assigned an attunement when a split starts depending on the section they stood in.
          </p>
          <p>
            Each colored guardian has a circular aura which significantly damages players with a different attunement.
          </p>
          <template #details>
            <span class="title is-6">Aura range</span>
            <p>
              The guardian auras have a range of 1500 units.
            </p>
            <span class="title is-6">Timing</span>
            <p>
              The guardian auras deal damage every TODO seconds.
            </p>
          </template>
        </mechanic-card>
      </div>
      <div class="tile is-4 is-vertical is-parent">
        <mechanic-card
            class="tile is-child"
            title="Small orbs"
            subtitle="Bullet Storm"
            :img="require('../assets/bullet-storm.jpg')"
            img-alt="Bullet Storm"
            evadable
        >
          <p>
            Small moving orbs dealing a small amount of damage.
          </p>
          <template #details>
            <span class="title is-6">Location</span>
            <p>
              The orbs appear at the center and on the edge of the arena, traveling in a straight path towards a player.
            </p>
            <span class="title is-6">TODO</span>
            <p>
              TODO: Is this blockable?
            </p>
          </template>
        </mechanic-card>
      </div>
    </div>

    <p class="title is-3">TODO</p>
    <div class="tile is-ancestor">
      <div class="tile is-4 is-vertical is-parent">
        <div class="tile is-child card">
          <div class="card-content">
            <div class="content">
              <ul>
                <li>Punches (?)</li>
                <li>Stolen Skills</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <p class="title is-3">Skritt Notes</p>
    <p class="subtitle is-6">A collection of random thoughts on Vale Guardian</p>
    <div class="tile is-ancestor">
      <div class="tile is-4 is-vertical is-parent">
        <div class="tile is-child card">
          <div class="card-content">
            <div class="content">
              <ul>
                <li>Dodging back on start leads to death.</li>
                <li>You can safely dodge into the wall.</li>
                <li>Vale Guardian does not attack the tank if they are at full health.</li>
                <li>The Punch auto-attack hits hard, and it often happens right after a green hits.</li>
                <li>Green likes to walk towards the red if people are only at red/blue.</li>
                <li>Green damage does not appear in arcdps logs.</li>
                <li>There is a standard damage aura cast by Vale Guardian that hits every three seconds.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MechanicCard from '../components/MechanicCard'
import DefianceBar from "@/submodules/raid-guides/components/DefianceBar";
import ClickableImage from "@/submodules/raid-guides/components/ClickableImage";

export default {
  name: "ValeGuardian",
  components: {ClickableImage, MechanicCard, DefianceBar},
}
</script>

<style scoped>

</style>